import { Box, IconButton, Typography, Button, Divider, CloseIcon } from "src/components"
import { useIsMobile } from "src/hooks"
import { useParams } from "react-router-dom";
import { useMessagingStore } from "src/stores";
import { useState } from "react";

import ChatPreviewCard from './chat-preview-card';
import ChatSidebarFooter from './chat-sidebar-footer';

import logoRect from 'src/assets/logo-rect.png';

export default function ChatSidebarContent({ 
    toggleSidebar, 
    handleAiClick, 
    handleChatClick, 
    handleGroupClick,
    chats,
    groups,
    chatUpdates }) {

    const isMobile = useIsMobile()
    const { userId, groupId } = useParams();

    const isGroupsEnabled = import.meta.env.VITE_FEATURE_GROUP_CHATS === 'true';

    const [visibleCount, setVisibleCount] = useState(3);
    const [visibleGroupCount, setVisibleGroupCount] = useState(3);

    const lastReadTimestamps = useMessagingStore((s) => s.lastReadTimestamps);

    const handleSeeMore = () => {
        setVisibleCount(prev => prev + 10);
    };

    const handleSeeMoreGroups = () => {
        setVisibleGroupCount(prev => prev + 10);
    };

    const sortedChats = [...chats].sort((a, b) => {
        // Sort descending: most recent first
        const dateA = new Date(a.lastMessageSentAt || 0).getTime();
        const dateB = new Date(b.lastMessageSentAt || 0).getTime();
        return dateB - dateA;
    });

    const visibleChats = sortedChats.slice(0, visibleCount);

    const sortedGroups = [...groups].sort((a, b) => {
        const dateA = new Date(a.lastMessageSentAt || 0).getTime();
        const dateB = new Date(b.lastMessageSentAt || 0).getTime();
        return dateB - dateA;
    });

    const visibleGroups = sortedGroups.slice(0, visibleGroupCount);

    return (
        <Box
            sx={{
                width: isMobile ? "80vw" : 320,
                maxWidth: isMobile ? "90vw" : 320,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s ease",
                boxShadow: isMobile ? 3 : "0 2px 0px rgba(0,0,0,0.1)"
            }}>
            {isMobile && (
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        bgcolor: "#fff",
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #e0e0e0",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                    }}>
                    <img src={logoRect} alt="Logo" style={{ height: 48 }} />
                    <IconButton
                        onClick={toggleSidebar}
                        sx={{
                        color: "#666",
                        "&:hover": { bgcolor: "#f5f5f5" },
                        "&:focus": { outline: "none" },
                        }}
                        disableRipple>
                        <CloseIcon />
                    </IconButton>
                </Box>
            )}

            <Box
                className="scrollBox"
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 2,
                    pb: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    height: '100%'
                    }}>
                <Box sx={{flexGrow: 1}}>
                    <Box
                        sx={{
                        bgcolor: "#fff",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        p: 3,
                        mb: 2
                        }}>
                        <Typography variant="h6" sx={{ color: "#333", mb: 1 }}>
                            AI Assistant
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                            Get instant help with your conversations and boost your productivity.
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="secondary" 
                            size="small" 
                            fullWidth
                            onClick={handleAiClick}>
                            Try AI
                        </Button>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" sx={{ color: "#333", mb: 0.5, fontWeight: 600 }}>
                            Chats
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                            Find other users in the map to start a new chat.
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {visibleChats.map((chat) => (
                                <ChatPreviewCard
                                    key={chat.id}
                                    chat={chat}
                                    isActive={chat.id === userId}
                                    lastRead={lastReadTimestamps[chat.id]}
                                    chatUpdateTimestamp={chatUpdates[chat.id]?.timestamp}
                                    onClick={handleChatClick}
                                />
                            ))}
                            {visibleCount < chats.length && (
                                <Divider sx={{ mt: 1, cursor: "pointer" }}>
                                    <Typography onClick={() => handleSeeMore()} variant="body2" color="textSecondary">
                                        See more chats
                                    </Typography>
                                </Divider>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{mt: 2, mb: 3}}>
                        <Typography variant="subtitle1" sx={{ color: "#333", mb: 2, fontWeight: 600 }}>
                            Groups
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {isGroupsEnabled ? (<>
                                {groups.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">
                                        No groups yet
                                    </Typography>
                                )}
                                {visibleGroups.map((group) => (
                                    <ChatPreviewCard
                                        key={group.id}
                                        chat={group} 
                                        isActive={group.id === groupId}
                                        lastRead={lastReadTimestamps[group.id]}
                                        chatUpdateTimestamp={chatUpdates[group.id]?.timestamp}
                                        onClick={handleGroupClick}
                                        isGroup={true}
                                    />
                                ))}
                                {visibleGroupCount < groups.length && (
                                    <Divider sx={{ mt: 1, cursor: "pointer" }}>
                                        <Typography
                                            onClick={handleSeeMoreGroups}
                                            variant="body2"
                                            color="textSecondary">
                                            See more groups
                                        </Typography>
                                    </Divider>
                                )}
                            </>) : (
                                <Box
                                    sx={{
                                        bgcolor: "#fff",
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 2,
                                        p: 3,
                                        height: '100px'
                                    }}>
                                    <Typography variant="h5" sx={{height: '100%', textAlign: 'center'}}>
                                        Coming Soon!
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>

                <ChatSidebarFooter />
            </Box>
        
        </Box>
    )
}
